import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, Plus, FolderTree } from 'lucide-react';
import { confirmDialog } from '../components/ConfirmHost';
import { API, errText } from '../components/doc-library/docLibShared';
import { DocCategoriesDialog } from '../components/doc-library/DocCategoriesDialog';
import { DocFormDialog } from '../components/doc-library/DocFormDialog';
import { AdminDocCard } from '../components/doc-library/AdminDocCard';
import { DocFilters, filterDocs, countByCat } from '../components/doc-library/DocFilters';

const names = (ids, list) => (ids?.length ? ids.map(i => list.find(x => x.id === i)?.name).filter(Boolean).join(', ') : 'Tümü');
const upload = (url, fields) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
  return axios.post(url, fd);
};

export default function DocLibraryPage() {
  const { user, canView, canTab } = useAuth();
  const allowed = canView('doc_library');
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [catOpen, setCatOpen] = useState(false);
  const [form, setForm] = useState({ mode: null, doc: null });
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const load = () => axios.get(`${API}/doc-library`).then(r => setDocs(r.data)).catch(() => toast.error('Dokümanlar yüklenemedi'));
  const loadCats = () => axios.get(`${API}/doc-library/categories`).then(r => setCategories(r.data)).catch(() => {});
  useEffect(() => {
    if (!allowed) return;
    load(); loadCats();
    axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {});
  }, [allowed]);

  const save = async ({ file, note, ...f }) => {
    try {
      if (form.mode === 'create') await upload(`${API}/doc-library`, { file, title: f.title, category_id: f.category_id, location_ids: JSON.stringify(f.location_ids) });
      else if (form.mode === 'version') await upload(`${API}/doc-library/${form.doc.id}/versions`, { file, note });
      else await axios.put(`${API}/doc-library/${form.doc.id}`, f);
      toast.success(form.mode === 'version' ? 'Yeni sürüm yüklendi' : 'Doküman kaydedildi');
      setForm({ mode: null, doc: null });
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const toggle = async (d, is_active) => {
    try { await axios.put(`${API}/doc-library/${d.id}`, { title: d.title, category_id: d.category_id, location_ids: d.location_ids, is_active }); load(); } catch (e) { toast.error(errText(e)); }
  };
  const remove = async (d) => {
    if (!(await confirmDialog({ title: 'Doküman silinsin mi?', description: `"${d.title}", tüm sürümleri ve indirme kayıtları silinecek.`, confirmLabel: 'Evet, Sil' }))) return;
    try { await axios.delete(`${API}/doc-library/${d.id}`); toast.success('Doküman silindi'); load(); } catch (e) { toast.error(errText(e)); }
  };
  const newDoc = () => (categories.length ? setForm({ mode: 'create', doc: null }) : (toast.info('Önce bir kategori ekleyin'), setCatOpen(true)));

  if (!allowed) return <p className="text-slate-500">Bu sayfa sadece Süper Admin ve İSG Uzmanı kullanıcılar içindir.</p>;
  const shown = filterDocs(docs, q, cat);
  return (
    <div className="space-y-6 max-w-5xl" data-testid="doc-library-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><BookOpen className="h-6 w-6 text-orange-500" />Dokümantasyon</h1>
          <p className="text-slate-500 mt-1 max-w-2xl">Firmaların erişebileceği prosedür, form ve talimatları yükleyin. Lokasyona göre hedefleyin, yeni sürüm yükleyerek güncel tutun ve hangi firmanın indirdiğini takip edin.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCatOpen(true)} data-testid="doc-categories-btn"><FolderTree className="h-4 w-4 mr-1" />Kategoriler</Button>
          <Button onClick={newDoc} className="bg-slate-900 hover:bg-slate-800" data-testid="doc-add-btn"><Plus className="h-4 w-4 mr-1" />Yeni Doküman</Button>
        </div>
      </div>
      <DocFilters q={q} setQ={setQ} cat={cat} setCat={setCat} categories={categories} counts={countByCat(docs)} />
      {shown.length === 0
        ? <Card className="border-dashed"><CardContent className="p-10 text-center text-slate-500" data-testid="doc-empty">{docs.length ? 'Aramanıza uygun doküman yok.' : 'Henüz doküman yüklenmedi.'}</CardContent></Card>
        : <div className="space-y-3" data-testid="doc-list">{shown.map(d => (
          <AdminDocCard key={d.id} doc={d} catName={categories.find(c => c.id === d.category_id)?.name || '-'} locNames={names(d.location_ids, locations)}
            onEdit={(doc) => setForm({ mode: 'edit', doc })} onVersion={(doc) => setForm({ mode: 'version', doc })} onDelete={remove} onToggle={toggle} />
        ))}</div>}
      <DocCategoriesDialog open={catOpen} categories={categories} onClose={() => setCatOpen(false)} onChanged={loadCats} />
      <DocFormDialog mode={form.mode} doc={form.doc} categories={categories} locations={locations} onClose={() => setForm({ mode: null, doc: null })} onSave={save} />
    </div>
  );
}
